import { create } from "zustand";
import {
  fetchApi,
  getLoginUrl,
  getAuthToken,
  saveAuthToken,
  AUTH_TOKEN_KEY,
} from "@/lib/api";
import {
  DashboardInviteStatus,
  Permission,
  StatusReportTemplateKey,
  UpdateMyProfileBody,
  canEditOwnedEntity,
  type UserStatusReportSelection,
} from "@forgepoint/shared";
import { useEffect } from "react"; // Added for useRequireAuth
import {
  identify,
  identifyOrganization,
  reset,
  captureException,
} from "@/lib/analytics";

// Define the user type
export interface Organization {
  id: string;
  name: string;
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  dashboardInviteStatus: DashboardInviteStatus;
  statusReportTemplate: UserStatusReportSelection;
  quoteViewNotificationsEnabled: boolean;
  profilePictureUrl: string | null;
  organization: Organization;
}

// Define the state shape
interface AuthState {
  user: User | null;
  /** Permissions granted by the user's role, from GET /users/me. */
  permissions: Permission[];
  isLoading: boolean;
}

// Define the actions
interface AuthActions {
  refreshUser: () => Promise<void>;
  login: (token: string) => Promise<void>;
  initializeAuth: () => void; // Added for explicit initialization
  /** Saves the user's own status-report selection via PUT /users/me. */
  updateStatusReportSelection: (
    selection: UserStatusReportSelection,
  ) => Promise<boolean>;
  /** Updates the caller's own name / profile picture. Returns true on success. */
  updateProfile: (body: UpdateMyProfileBody) => Promise<boolean>;
}

// Define the initial state
const initialState: AuthState = {
  user: null,
  permissions: [],
  isLoading: true, // Start as true until initialization
};

// Create the Zustand store
export const useAuthStore = create<AuthState & AuthActions>()((set, get) => ({
  ...initialState,

  // Function to fetch user data using the token
  refreshUser: async () => {
    // Only set loading if not already loading to prevent flicker
    if (!get().isLoading) {
      set({ isLoading: true });
    }
    let userData: User | null = null;
    let userPermissions: Permission[] = [];
    try {
      const token = getAuthToken(); // Get token inside the function
      if (!token) {
        set({ user: null, permissions: [], isLoading: false });
        return;
      }

      const response = await fetchApi("users/me", "GET");

      if (!response.ok) {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        set({ user: null, permissions: [], isLoading: false });
        reset();
        return;
      }

      const responseData = response.data as {
        user?: User;
        permissions?: Permission[];
      };
      if (responseData && responseData.user) {
        const { user } = responseData;
        userData = {
          id: user.id,
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email,
          role: user.role,
          dashboardInviteStatus:
            user.dashboardInviteStatus ?? DashboardInviteStatus.NOT_INVITED,
          statusReportTemplate:
            user.statusReportTemplate ?? StatusReportTemplateKey.STANDARD,
          quoteViewNotificationsEnabled:
            user.quoteViewNotificationsEnabled ?? true,
          profilePictureUrl: user.profilePictureUrl ?? null,
          organization: user.organization,
        };
        userPermissions = responseData.permissions ?? [];

        identify({
          userId: user.id,
          email: user.email,
          name: user.firstName + " " + user.lastName,
        });
        if (user.organization?.id) {
          identifyOrganization(user.organization.id, {
            name: user.organization.name,
          });
        }
      } else {
        // Handle case where response is ok but user data is missing
        localStorage.removeItem(AUTH_TOKEN_KEY); // Treat as invalid session
      }
    } catch (error) {
      captureException(error as Error);
      localStorage.removeItem(AUTH_TOKEN_KEY); // Also clear token on error
    } finally {
      set({ user: userData, permissions: userPermissions, isLoading: false });
    }
  },

  // Function to log in
  login: async (token: string) => {
    saveAuthToken(token);
    await get().refreshUser(); // refreshUser handles isLoading state
  },

  updateStatusReportSelection: async (selection) => {
    const current = get().user;
    if (!current) {
      return false;
    }
    const response = await fetchApi("users/me", "PUT", {
      body: { statusReportTemplate: selection },
    });
    if (!response.ok) {
      return false;
    }
    set({ user: { ...current, statusReportTemplate: selection } });
    return true;
  },
  updateProfile: async (body: UpdateMyProfileBody) => {
    try {
      const response = await fetchApi("users/me", "PUT", { body });
      if (!response.ok) {
        return false;
      }
      await get().refreshUser();
      return true;
    } catch (error) {
      captureException(
        error instanceof Error ? error : new Error(String(error)),
      );
      return false;
    }
  },

  // Function to initialize auth state (call this once when app loads)
  initializeAuth: () => {
    // Check token existence *before* deciding to refresh
    const token = getAuthToken();
    if (token && get().user === null && get().isLoading) {
      get().refreshUser();
    } else if (!token) {
      set({ isLoading: false, user: null }); // Ensure state is not loading if no token
    } else if (get().user !== null && get().isLoading) {
      // If already loaded (user exists), ensure loading is false
      set({ isLoading: false });
    }
  },
}));

// Selector for derived state
export const useIsAuthenticated = () => useAuthStore((state) => !!state.user);

/**
 * Checks whether the current user's role grants a permission.
 * @param permission - The permission to check (e.g. Permission.SETTINGS_EDIT)
 * @returns Whether the authenticated user holds the permission
 */
export const useHasPermission = (permission: Permission): boolean =>
  useAuthStore((state) => state.permissions.includes(permission));

/**
 * Checks whether the current user's role grants any of the permissions, e.g.
 * the org-wide or owner-scoped variant of a capability.
 * @param permissions - The permissions to check
 * @returns Whether the authenticated user holds at least one of them
 */
export const useHasAnyPermission = (...permissions: Permission[]): boolean =>
  useAuthStore((state) =>
    permissions.some((permission) => state.permissions.includes(permission)),
  );

/**
 * Checks whether the current user may edit an owned record: allowed with the
 * org-wide permission, or with the owner-scoped permission when they own it.
 * @param editAll - The org-wide edit permission
 * @param editOwn - The owner-scoped edit permission
 * @param ownerUserIds - The record's owner user id(s); the user owns the
 *   record when any of them matches
 * @returns Whether the authenticated user may edit the record
 */
export const useCanEditOwned = (
  editAll: Permission,
  editOwn: Permission,
  ownerUserIds?: string | null | (string | null | undefined)[],
): boolean =>
  useAuthStore((state) => {
    const owners = Array.isArray(ownerUserIds) ? ownerUserIds : [ownerUserIds];
    return canEditOwnedEntity(
      state.permissions.includes(editAll),
      state.permissions.includes(editOwn),
      !!state.user?.id && owners.includes(state.user.id),
    );
  });

// Hook to check if the user is logged in and redirect if not
// This replaces the old useRequireAuth hook
export function useRequireAuth() {
  const isAuthenticated = useIsAuthenticated();
  const isLoading = useAuthStore((state) => state.isLoading);

  useEffect(() => {
    // Only redirect when loading is finished and user is not authenticated
    if (!isLoading && !isAuthenticated) {
      // Check again for token just before redirecting, might have been set async
      if (!getAuthToken()) {
        window.location.href = getLoginUrl();
      }
    }
  }, [isAuthenticated, isLoading]);

  // Return loading state: true if initial loading is happening OR if user is not authenticated (as redirect might be pending)
  return { isLoading: isLoading || !isAuthenticated };
}

// It's generally better to explicitly call initializeAuth from the root component (e.g., App.tsx)
// This ensures it runs after the app is mounted and avoids potential race conditions.
// Example in App.tsx:
// useEffect(() => {
//   useAuthStore.getState().initializeAuth();
// }, []);
